import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { InventoryLayout } from '../../components/inventory/InventoryLayout';
import { ProductCard } from '../../components/inventory/ProductCard';
import { inventoryCategories } from '../../data/inventory';
import { products } from '../../data/products';
export function InventoryCategoryPage() {
  const {
    categorySlug
  } = useParams<{
    categorySlug: string;
  }>();
  const location = useLocation();
  // Parse initial search query from URL
  const queryParams = new URLSearchParams(location.search);
  const initialQuery = queryParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [sortOrder, setSortOrder] = useState('name-asc');
  const [activeSector, setActiveSector] = useState('All');
  // Update search query if URL changes
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q');
    if (q !== null) {
      setSearchQuery(q);
    }
  }, [location.search]);
  // Determine if we are in "Browse All" mode or specific category mode
  const isBrowseAll = !categorySlug;
  const category = isBrowseAll ? null : inventoryCategories.find(c => c.slug === categorySlug);
  // Redirect if a category slug was provided but not found
  if (categorySlug && !category) {
    return <Navigate to="/" replace data-id="element-415" />;
  }
  // Base products for this page
  const baseProducts = isBrowseAll ? products : products.filter(p => p.category === categorySlug);
  // Extract unique sectors from base products for the filter
  const availableSectors = useMemo(() => {
    const sectors = new Set<string>();
    baseProducts.forEach(p => {
      p.sectors.forEach(s => {
        // Capitalize first letter for display
        sectors.add(s.charAt(0).toUpperCase() + s.slice(1));
      });
    });
    return ['All', ...Array.from(sectors).sort()];
  }, [baseProducts]);
  // Apply filters and sorting
  const displayProducts = useMemo(() => {
    let result = [...baseProducts];
    // 1. Search Filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(query) || p.shortDescription.toLowerCase().includes(query) || p.technicalDescription.toLowerCase().includes(query));
    }
    // 2. Sector Filter
    if (activeSector !== 'All') {
      const sectorLower = activeSector.toLowerCase();
      result = result.filter(p => p.sectors.includes(sectorLower));
    }
    // 3. Sort
    result.sort((a, b) => {
      if (sortOrder === 'name-asc') {
        return a.name.localeCompare(b.name);
      } else if (sortOrder === 'name-desc') {
        return b.name.localeCompare(a.name);
      }
      return 0;
    });
    return result;
  }, [baseProducts, searchQuery, activeSector, sortOrder]);
  // Page content based on mode
  const pageTitle = isBrowseAll ? 'Browse All Products' : category?.name;
  const pageDescription = isBrowseAll ? 'View our complete inventory catalogue.' : category?.description;
  return <InventoryLayout data-id="element-416">
      {/* Header */}
      <div className="bg-white border-b border-steel-100 py-8 sm:py-10" data-id="element-417">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-418">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6" data-id="element-419">
            <div className="flex-1" data-id="element-420">
              <span className="text-steel-500 font-bold uppercase tracking-widest text-xs mb-2 block" data-id="element-421">
                Inventory / {isBrowseAll ? 'All Products' : category?.name}
              </span>
              <div className="flex items-center gap-3 mb-1 sm:mb-2 flex-wrap" data-id="element-422">
                <h1 className="text-2xl sm:text-3xl font-bold text-navy-900" data-id="element-423">
                  {pageTitle}
                </h1>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold bg-slate-100 text-navy-900 border border-steel-200" data-id="element-424">
                  {displayProducts.length}{' '}
                  {displayProducts.length === 1 ? 'item' : 'items'}
                </span>
              </div>
              <p className="text-sm sm:text-base text-steel-600 max-w-2xl" data-id="element-425">
                {pageDescription}
              </p>
            </div>

            {/* Search Input */}
            <div className="w-full lg:w-80 flex-shrink-0" data-id="element-426">
              <div className="relative" data-id="element-427">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-428">
                  <Search className="h-4 w-4 text-steel-400" data-id="element-429" />
                </div>
                <input type="text" className="block w-full pl-10 pr-3 py-2.5 border border-steel-300 rounded-sm leading-5 bg-white placeholder-steel-400 focus:outline-none focus:ring-1 focus:ring-cg-orange-600 focus:border-cg-orange-600 sm:text-sm transition-shadow" placeholder="Search products..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-430" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar: Sort & Filter */}
      <div className="bg-white border-b border-steel-100 py-3 sticky top-[116px] z-30 shadow-sm" data-id="element-431">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-432">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-433">
            {/* Sector Filters */}
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-434">
              <span className="text-xs font-bold text-steel-500 uppercase tracking-wider mr-2 flex-shrink-0" data-id="element-435">
                Sector:
              </span>
              {availableSectors.map(sector => <button key={sector} onClick={() => setActiveSector(sector)} className={`flex-shrink-0 px-3 py-1.5 text-xs font-bold rounded-sm transition-colors ${activeSector === sector ? 'bg-navy-900 text-white' : 'bg-white border border-steel-200 text-steel-600 hover:border-steel-400 hover:text-navy-900'}`} data-id="element-436">
                  {sector}
                </button>)}
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-3 flex-shrink-0" data-id="element-437">
              <label htmlFor="sort" className="text-xs font-bold text-steel-500 uppercase tracking-wider" data-id="element-438">
                Sort:
              </label>
              <select id="sort" value={sortOrder} onChange={e => setSortOrder(e.target.value)} className="block w-full pl-3 pr-10 py-1.5 text-sm border-steel-300 focus:outline-none focus:ring-cg-orange-600 focus:border-cg-orange-600 sm:text-sm rounded-sm bg-white border text-navy-900 font-medium" data-id="element-439">
                <option value="name-asc" data-id="element-440">Name A–Z</option>
                <option value="name-desc" data-id="element-441">Name Z–A</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Product Grid */}
      <div className="bg-slate-50 py-8 sm:py-12 min-h-[50vh]" data-id="element-442">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-443">
          {displayProducts.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6" data-id="element-444">
              {displayProducts.map(product => <ProductCard key={product.slug} product={product} data-id="element-445" />)}
            </div> : <div className="text-center py-16 sm:py-20 bg-white border border-steel-200 rounded-sm" data-id="element-446">
              <Search className="w-12 h-12 text-steel-300 mx-auto mb-4" data-id="element-447" />
              <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-448">
                No products found
              </h3>
              <p className="text-steel-500 max-w-md mx-auto mb-6" data-id="element-449">
                We couldn't find any products matching your current search and
                filter criteria.
              </p>
              <button onClick={() => {
            setSearchQuery('');
            setActiveSector('All');
          }} className="inline-flex items-center justify-center px-4 py-2 border border-steel-300 shadow-sm text-sm font-bold rounded-sm text-navy-900 bg-white hover:bg-slate-50 focus:outline-none transition-colors" data-id="element-450">
                Clear Filters
              </button>
            </div>}
        </div>
      </div>
    </InventoryLayout>;
}